// arise-frontend/src/pages/LoginPage.jsx
// On success the shared auth state flips to 'authenticated' and <GuestRoute>
// redirects to the validated destination (location.state.from, else /analyze).
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthCard, { AuthField, AuthAlert } from '../components/auth/AuthCard';
import { useAuth } from '../context/AuthContext';
import { authErrorMessage } from '../services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(email, password) {
  const errs = {};
  const e = email.trim();
  if (!e) errs.email = 'Enter your email address.';
  else if (e.length > 255 || !EMAIL_RE.test(e)) errs.email = 'Enter a valid email address.';
  if (!password) errs.password = 'Enter your password.';
  else if (password.length > 128) errs.password = 'Password must be at most 128 characters.';
  return errs;
}

export default function LoginPage() {
  const { login, notice } = useAuth();
  const location = useLocation();
  const navState = location.state || {};
  const from = typeof navState.from === 'string' ? navState.from : undefined;
  const registeredEmail = typeof navState.registeredEmail === 'string' ? navState.registeredEmail : '';

  const [email, setEmail]           = useState(registeredEmail);
  const [password, setPassword]     = useState('');
  const [errors, setErrors]         = useState({});
  const [formError, setFormError]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    const found = validate(email, password);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) return;

    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setFormError(authErrorMessage(err, 'login'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Log In"
      subtitle="Sign in to run analyses with ARISE"
      footer={
        <>
          <span>
            No account?{' '}
            <Link to="/register" state={{ from }} style={{ color: '#00f0ff' }}>Register</Link>
          </span>
          <Link to="/" style={{ color: '#6b8fa8' }}>← Back to home</Link>
        </>
      }
    >
      {registeredEmail && <AuthAlert kind="success">Account created. Log in to continue.</AuthAlert>}
      {notice && <AuthAlert kind="warn">{notice}</AuthAlert>}
      {formError && <AuthAlert kind="error">{formError}</AuthAlert>}

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          id="login-email" label="Email" type="email" name="email"
          autoComplete="username" inputMode="email" maxLength={255}
          value={email} onChange={ev => setEmail(ev.target.value)}
          error={errors.email} disabled={submitting}
        />
        <AuthField
          id="login-password" label="Password" type="password" name="password" reveal
          autoComplete="current-password" maxLength={128}
          value={password} onChange={ev => setPassword(ev.target.value)}
          error={errors.password} disabled={submitting}
        />
        <button
          type="submit" className="btn-primary" disabled={submitting}
          style={{ width: '100%', marginTop: '0.5rem' }}
        >
          {submitting ? 'Signing in…' : 'Log In'}
        </button>
      </form>
    </AuthCard>
  );
}
